export function siblingIndex(element) {
    let i = 0;

    while ((element = element.previousElementSibling) !== null) {
        i += 1;
    }

    return i;
}

export function keySpline(mX1, mY1, mX2, mY2) {
    function get(aX) {
        if (mX1 === mY1 && mX2 === mY2) {
            return aX;
        }

        return calcBezier(getTForX(aX), mY1, mY2);
    }

    function a(aA1, aA2) {
        return 1.0 - 3.0 * aA2 + 3.0 * aA1;
    }

    function b(aA1, aA2) {
        return 3.0 * aA2 - 6.0 * aA1;
    }

    function c(aA1) {
        return 3.0 * aA1;
    }

    function calcBezier(aT, aA1, aA2) {
        return ((a(aA1, aA2) * aT + b(aA1, aA2)) * aT + c(aA1)) * aT;
    }

    function getSlope(aT, aA1, aA2) {
        return 3.0 * a(aA1, aA2) * aT * aT + 2.0 * b(aA1, aA2) * aT + c(aA1);
    }

    function getTForX(aX) {
        let aGuessT = aX;

        for (let i = 0; i < 4; i++) {
            const currentSlope = getSlope(aGuessT, mX1, mX2);

            if (currentSlope === 0.0) {
                return aGuessT;
            }
            const currentX = calcBezier(aGuessT, mX1, mX2) - aX;

            aGuessT -= currentX / currentSlope;
        }

        return aGuessT;
    }

    return {
        get
    };
}

export function prefixedEventListener(element, type, callback, options) {
    const prefixes = ['webkit', 'moz', 'MS', 'o', ''];

    prefixes.forEach(prefix => {
        if (!prefix) {
            type = type.toLowerCase();
        }
        element.addEventListener(prefix + type, callback, options);
    });
}

/**
 * Returns dom object or array of dom objects based on html string.
 * @param htmlString
 * @returns {*}
 */
export function parseHtmlToDom(htmlString) {
    const wrap = document.createElement('div');

    wrap.innerHTML = htmlString;

    if (wrap.children.length === 1) {
        return wrap.children[0];
    }

    return wrap.children;
}

export function closest(elm, targetClass) {
    if (elm) {
        while (elm) {
            if (elm.classList && elm.classList.contains(targetClass)) {
                break;
            } else if (elm.tagName === 'HTML') {
                return false;
            }

            elm = elm.parentNode;
        }
    }

    return elm;
}

export function offsetTop(elem) {
    let offsetLeft = 0;

    do {
        if (!Number.isNaN(elem.offsetTop)) {
            offsetLeft += elem.offsetTop;
        }
    } while ((elem = elem.offsetParent));

    return offsetLeft;
}

export function toggle(node, toggleValue) {
    if (toggleValue === undefined) {
        toggleValue = node.hasAttribute('hidden');
    }

    if (toggleValue) {
        node.removeAttribute('hidden');
    } else {
        node.setAttribute('hidden', '');
    }
}

export function scrollTo(targetElement, extraOffset = -20, duration = 1000, callback = false) {
    const { width } = getViewport();
    const headerOffset = width > 960 ? -65 : -45;
    const doc = document.documentElement;

    let targetOffset = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    const from = targetOffset;
    const to = offsetTop(targetElement) + extraOffset + headerOffset;

    const relativeOffset = to - from;
    let start = null;
    const easeInOut = keySpline(0.42, 0, 0.58, 1);

    function step(timestamp) {
        if (!start) {
            start = timestamp;
        }

        const progress = (timestamp - start) / duration;
        const yProgress = easeInOut.get(progress);

        targetOffset = Math.round(relativeOffset * yProgress + from);

        if (progress < 1) {
            window.scrollTo(0, targetOffset);
            window.requestAnimationFrame(step);
        } else {
            window.scrollTo(0, to);
            if (typeof callback === 'function') {
                callback();
            }
        }
    }

    window.requestAnimationFrame(step);
}

/**
 * Simple form data extractor
 * Extracts only single select fields and single checkboxes
 * @param formReference
 * @returns {string} url encoded
 */
export function extractFormData(formReference) {
    const formData = new FormData(formReference);

    return [...formData]
        .filter(([, value]) => value)
        .reduce((entries, [key, value]) => {
            entries.push({ key, value });

            return entries;
        }, []);
}

export function extractSearchFormData(formReference) {
    const formData = extractFormData(formReference);
    // if there is a postalcode or city field present, but not distance, remove the distance field
    if (formData.some(f => f.key === 'PostalCodeCity') && !formData.some(f => f.key === 'Distance')) {
        return formData.filter(f => f.key !== 'PostalCodeCity');
    }
    return formData;
}

export function serializeFormData(formData) {
    const urlParams = [];

    formData.forEach(({ key, value }) => urlParams.push(`${key}=${encodeURIComponent(value)}`));

    return urlParams.join('&');
}

export function touchesElement(sourceEl, targetEl, offset = 0) {
    const sourceElRect = sourceEl.getBoundingClientRect();
    const targetElRect = targetEl.getBoundingClientRect();

    const overlap = !(
        sourceElRect.right < targetElRect.left - offset ||
        sourceElRect.left > targetElRect.right + offset ||
        sourceElRect.bottom < targetElRect.top - offset ||
        sourceElRect.top > targetElRect.bottom + offset
    );

    return overlap;
}

export function touchesViewport(el, offset = 0) {
    if (el) {
        const rect = el.getBoundingClientRect();
        const targetTop = rect.top - offset;
        const targetBottom = rect.bottom;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const topInViewport = targetTop >= 0 && targetTop < viewportHeight;
        const bottomInViewport = targetBottom >= 0 && targetBottom < viewportHeight;

        return topInViewport || bottomInViewport;
    }

    return false;
}

export function camelToKebab(str) {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

export function kebabToCamel(str) {
    return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
}

export function createPubSub() {
    const subscriptions = {};

    const subscribe = (topic, cb) => {
        if (typeof subscriptions[topic] === 'undefined') {
            subscriptions[topic] = [];
        }

        const subscriptionIndex = subscriptions[topic].push(cb) - 1;

        return {
            unsubscribe: () => {
                subscriptions[topic].splice(subscriptionIndex, 1);
            }
        };
    };

    const publish = (topic, data = {}) => {
        if (typeof subscriptions[topic] !== 'undefined' && subscriptions[topic].constructor === Array) {
            subscriptions[topic].forEach(callBack => {
                try {
                    callBack(data);
                } catch (e) {
                    console.error(e);
                }
            });
        }
    };

    return {
        subscribe,
        publish
    };
}

export function isInFullScreen() {
    return (
        (typeof document.fullscreenElement !== 'undefined' && document.fullscreenElement !== null) ||
        (typeof document.webkitFullscreenElement !== 'undefined' && document.webkitFullscreenElement !== null) ||
        (typeof document.mozFullScreenElement !== 'undefined' && document.mozFullScreenElement !== null) ||
        (typeof document.msFullscreenElement !== 'undefined' && document.msFullscreenElement !== null)
    );
}

export function enterFullScreen() {
    const docElm = document.documentElement;

    if (docElm.requestFullscreen) {
        docElm.requestFullscreen();
    } else if (docElm.mozRequestFullScreen) {
        docElm.mozRequestFullScreen();
    } else if (docElm.webkitRequestFullScreen) {
        docElm.webkitRequestFullScreen();
    } else if (docElm.msRequestFullscreen) {
        docElm.msRequestFullscreen();
    }
}

export function exitFullScreen() {
    if (isInFullScreen()) {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }
}

export function onFullScreenEnter(cb) {
    function handleFullScreenChange() {
        if (isInFullScreen()) {
            cb();
        }
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);
}

export function onFullScreenExit(cb) {
    function handleFullScreenChange() {
        if (!isInFullScreen()) {
            cb();
        }
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);
}

export function applyOperator(operator, x, y) {
    const operators = {
        '<': (a, b) => a < b,
        '>': (a, b) => a > b,
        '<=': (a, b) => a <= b,
        '>=': (a, b) => a >= b
    };

    return operators[operator](x, y);
}

export function triggerJs(componentName, module, context) {
    if (typeof context === 'undefined') {
        context = document;
    }

    context.querySelectorAll(`.${componentName}`).forEach(element => {
        if (!element.hasAttribute('data-instantiated')) {
            element.setAttribute('data-instantiated', '');
            element.jsApi = module(element);
        }
    });
}

export function getViewport() {
    return {
        width: Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
        height: Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
    };
}

export function getComputedStyle(node, cssAttribute) {
    return window.getComputedStyle(node, null).getPropertyValue(cssAttribute);
}

export function stopEventPropagation(nodes, eventName) {
    nodes.forEach(node => {
        if (node !== null) {
            node.addEventListener(eventName, e => e.stopPropagation());
        }
    });
}

export function isDisplayed(node) {
    return node.offsetParent !== null;
}

export function stripWhiteSpace(str) {
    return str.replace(/ /g, '');
}

export function parseJSON(jsonString) {
    try {
        const json = JSON.parse(jsonString);

        if (json && typeof json === 'object') {
            return json;
        }
    } catch (e) {
        return false;
    }

    return false;
}

export function arrRemoveValue(array, value) {
    const index = array.indexOf(value);

    if (index >= 0) {
        array.splice(index, 1);

        return true;
    }

    return false;
}

export function delegateEvent(selector, eventType, handler) {
    document.addEventListener(eventType, e => {
        const listeningTarget = e.target.closest(selector);

        if (listeningTarget) {
            handler(listeningTarget, e);
        }
    });
}

export function animateCSS({ node, animationName, animationSpeed = 'faster', callback }) {
    node.classList.add('animated', animationSpeed, animationName);

    function handleAnimationEnd() {
        node.classList.remove('animated', animationSpeed, animationName);
        node.removeEventListener('animationend', handleAnimationEnd);

        if (typeof callback === 'function') {
            callback();
        }
    }

    node.addEventListener('animationend', handleAnimationEnd);
}

export function getCookie(name) {
    const cookie = document.cookie.split(';').filter(item => item.trim().startsWith(`${name}=`))[0];

    if (!cookie) {
        return false;
    }

    const [key, value] = cookie.split('=');

    return {
        key,
        value
    };
}

export function setCookie({ name, value, days }) {
    const date = new Date();

    date.setTime(date.getTime() + 24 * 60 * 60 * 1000 * days);

    document.cookie = `${name}=${value};path=/;expires=${date.toGMTString()};SameSite=Lax`;
}

export function getPageDataKeyValue(key) {
    if (!window.viabovag.pageData) {
        return undefined;
    }

    return window.viabovag.pageData[key];
}

export function injectScript(scriptUrl, scriptId = '') {
    return new Promise((resolve, reject) => {
        if (scriptId && document.getElementById(scriptId)) {
            return;
        }

        const script = document.createElement('script');

        script.async = true;
        script.src = scriptUrl;

        if (scriptId) {
            script.id = scriptId;
        }

        script.addEventListener('load', resolve);
        script.addEventListener('error', () => reject(new Error('Loading script failed')));
        script.addEventListener('abort', () => reject(new Error('Loading script aborted')));

        document.head.appendChild(script);
    });
}

export function getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export function getSvgSpritePath() {
    const spriteNode = document.querySelector('use[*|href*="icons.sprite"]');
    const href = spriteNode?.getAttribute('xlink:href');
    const path = href?.split('#')[0];

    return path;
}

export function renderIconNode(iconName) {
    const html = `
        <svg class="icon-${iconName}">
            <use xlink:href="${getSvgSpritePath()}#${iconName}"></use>
        </svg>
    `;

    return parseHtmlToDom(html);
}

export function replaceIconPlaceholderNodes(cssSelector) {
    const iconPlaceholderNodes = document.querySelectorAll(cssSelector);

    iconPlaceholderNodes.forEach(iconPlaceholderNode => {
        const iconName = iconPlaceholderNode.textContent.trim();
        const iconNode = renderIconNode(iconName);

        iconPlaceholderNode.parentNode.replaceChild(iconNode, iconPlaceholderNode);
    });
}

export function elementOrParentsHasClass(element, className) {
    if (element.classList && element.classList.contains(className)) {
        return true;
    }
    return element.parentNode && elementOrParentsHasClass(element.parentNode, className);
}

/**
 * Checks array of classes on element or parent
 * @param {Element} element
 * @param {string[]} classNames
 * @returns {boolean}
 */
export const elementOrParentsHasOneOfClassNames = (element, classNames) => {
    return classNames.some(className => elementOrParentsHasClass(element, className));
};

export const getNextMobilityType = mobilityType => {
    switch (mobilityType) {
        case 'auto':
            return 'car';
        case 'fiets':
            return 'bicycle';
        case 'motor':
            return 'motor';
        case 'kampeervoertuig':
            return 'caramper';
        default:
            return 'car';
    }
};
